import {useCallback, useState} from "react";
import {type LayoutStorage, type PanelSize, usePanelRef} from "react-resizable-panels";

/** Panel sizes are 0–100 (45 = 45%), not 0–1; below this a panel counts as collapsed. */
export const COLLAPSED_PERCENT_MAX = 1;

export const isCollapsedSize = (size: PanelSize) => size.asPercentage < COLLAPSED_PERCENT_MAX;

/** Keeps dragged widths for the browser session; blocked storage just means no memory. */
export const sessionLayoutStorage: LayoutStorage = {
    getItem: (key) => {
        try {
            return sessionStorage.getItem(key);
        } catch {
            return null;
        }
    },
    setItem: (key, value) => {
        try {
            sessionStorage.setItem(key, value);
        } catch {
            // storage blocked: the layout still applies until reload
        }
    },
};

/**
 * A collapsible react-resizable-panels Panel that hides beside a full-screen dashboard: pass
 * `panelRef` and `onResize` to the Panel, read `collapsed`, and call `toggle` from the full-screen button.
 */
export function useCollapsiblePanel() {
    const panelRef = usePanelRef();
    const [collapsed, setCollapsed] = useState(false);

    const onResize = useCallback((size: PanelSize) => {
        const next = isCollapsedSize(size);
        setCollapsed(prev => (prev === next ? prev : next));
    }, []);

    const toggle = useCallback(() => {
        const panel = panelRef.current;
        if (!panel) return;
        if (panel.isCollapsed()) panel.expand();
        else panel.collapse();
    }, [panelRef]);

    return {panelRef, collapsed, setCollapsed, onResize, toggle};
}

export type CollapsiblePanel = ReturnType<typeof useCollapsiblePanel>;
